import { fundApi } from '@/modules/fund/services/bff/fund.api';
import { investmentApi } from '@/modules/investment/services/bff/investment.api';
import { portfolioApi } from '@/modules/portfolio/services/bff/portfolio.api';
import {
  listResearchCompanies,
  listResearchTopics,
} from '@/modules/research/services/bff/research.api';
import { isDirectCapitalAccount } from '@/shared/fundKind';

async function allPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; total: number }>,
): Promise<T[]> {
  const first = await fetchPage(1);
  const items = [...first.items];
  for (let page = 2; items.length < first.total; page++) {
    const next = await fetchPage(page);
    if (!next.items.length) break;
    items.push(...next.items);
  }
  return items;
}
export type SearchResult = {
  key: string;
  name: string;
  kind: string;
  path: string;
};
/** Only query granted, scoped business APIs. Failed sources cannot leak cached results. */
export async function searchPlatform(
  keyword: string,
  access: Record<string, boolean>,
) {
  const jobs: Array<{ label: string; run: () => Promise<SearchResult[]> }> = [];
  const add = (
    permission: string,
    label: string,
    run: () => Promise<SearchResult[]>,
  ) => {
    if (access[permission]) jobs.push({ label, run });
  };
  add('investment-projects:view', '投资项目', async () =>
    (await investmentApi.listProjects({ keyword })).map((x) => ({
      key: `project:${x.id}`,
      name: x.name,
      kind: '投资项目',
      path: `/projects/${encodeURIComponent(x.id)}`,
    })),
  );
  add('portfolio-company:view', '已投项目', async () =>
    (await portfolioApi.listCompanies()).map((x) => ({
      key: `portfolio:${x.id}`,
      name: x.name,
      kind: '已投项目',
      path: `/portfolio/portfolio/${encodeURIComponent(x.id)}`,
    })),
  );
  add('fundRecordModule', '基金', async () =>
    (await fundApi.listFunds()).map((x) => ({
      key: `fund:${x.uuid}`,
      name: x.name,
      // wire fundKind 是 'cvc' ⇒ 裸中文比较恒 false，直投账户此前一律标「基金」
      kind: isDirectCapitalAccount(x) ? '直投资金账户' : '基金',
      path: `/funds/fund/${encodeURIComponent(x.uuid)}`,
    })),
  );
  add('research-topics:view', '研究专题', async () => {
    const items = await allPages((page) =>
      listResearchTopics({ keyword, page, pageSize: 100 }),
    );
    return items.map((x) => ({
      key: `topic:${x.uuid}`,
      name: x.name,
      kind: '研究专题',
      path: `/research/research/topic/${encodeURIComponent(x.uuid)}`,
    }));
  });
  add('research-companies:view', '研究企业', async () => {
    const items = await allPages((page) =>
      listResearchCompanies({ keyword, page, pageSize: 100 }),
    );
    return items.map((x) => ({
      key: `company:${x.uuid}`,
      name: x.name,
      kind: '研究企业',
      path: `/research/company/${encodeURIComponent(x.uuid)}`,
    }));
  });
  const results = await Promise.allSettled(jobs.map((j) => j.run()));
  const term = keyword.trim().toLocaleLowerCase();
  return {
    items: results
      .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
      .filter((x) => x.name.toLocaleLowerCase().includes(term)),
    failedSources: results.flatMap((r, i) =>
      r.status === 'rejected' ? [jobs[i].label] : [],
    ),
  };
}
